import { combineReducers } from 'redux';
import { toast } from 'react-toastify';
/**
 * Product Groups struct used for proposal creator
 * @typedef {Object} ProductGroup
 * @property {string} _id - ID of product group
 * @property {string} name - Unique name identifier of product group
 * @property {string} description - Description of product group
 * @property {boolean} multiSelect - Specified if multiple products in the group can be
 * selected at the same time
 * @property {boolean} deleted - If the group is marked deleted by the backend
 * @property {boolean} enabled - If the user has enabled the product group.
 * Enabled group is shown in proposal
 * @property {Product[]} products
 *
 * Product struct used in proposal creator
 * @typedef {Object} Product
 * @property {string} name - Unique name of the product
 * @property {string} description - Description of product
 * @property {boolean} selected - UI state determines if the product is selected for proposal
 * @property {number} quantity - Number of products to be proposald
 * @property {PriceListItem[]} priceList
 *
 * Price List item for proposal products
 * @typedef {Object} PriceListItem
 * @property {string} key - unique key identifier for the price list bucket.
 * Key is shared for all price list items
 * @property {string} desc - Price List item description text
 * @property {number} price - Price for the price list item bucket
 */
const initialProposalEditorData = { proposalName: '', priceKey: {} };

const proposalEditor = (state = initialProposalEditorData, action) => {
  switch (action.type) {
    case 'INITIALIZE_PROPOSAL_EDITOR': {
      return { ...action.proposal };
    }
    case 'PROPOSAL_EDITOR_FIELD_UPDATE': {
      const { name, value } = action;
      return {
        ...state,
        [name]: value,
      };
    }
    default:
      return state;
  }
};

const initialProposalObject = { attachments: [] };

const proposalObject = (state = initialProposalObject, action) => {
  switch (action.type) {
    case 'INITIALIZE_PROPOSAL_OBJECT': {
      return { ...action.proposal };
    }
    default:
      return state;
  }
};

const initialProposalDisplayMode = 'edit';
const displayMode = (state = initialProposalDisplayMode, action) => {
  switch (action.type) {
    case 'SET_PROPOSAL_DISPLAY_MODE':
      return action.mode;
    case 'INITIALIZE_PROPOSAL_EDITOR':
      return initialProposalDisplayMode;
    default:
      return state;
  }
};

/**
 * Set initial box data for proposal box by setting up initial UI values.
 * @param {Array} productGroups - The product groups costing to be used for proposal
 * @param {number} index - Box index in proposals
 */

function getProposalBoxInitialData(
  productGroups,
  index,
  boxName,
  changeProduct,
  versionPrice,
  count,
  oneTimeCharge,
  installation,
) {
  var initData = {};
  var initOneTime = {};
  var initInstallation = {};
  if (oneTimeCharge) {
    if (Array.isArray(oneTimeCharge)) {
      oneTimeCharge.forEach(group => {
        if (!initOneTime[group.groupName]) {
          initOneTime[group.groupName] = {
            _id: group._id,
            name: group.groupName,
            description: group.description,
            multiSelect: group.multiSelect,
            deleted: group.deleted,
            enabled: false, // UI state
            products: [],
            count: 0,
          };
        }
        group.products.forEach(product => {
          initOneTime[group.groupName].products.push({
            ...product,
            changeProduct: 'Analog Lines',
            versionPrice: versionPrice,
            selected: false,
            quantity: 0,
            count: 0,
          });
        });
      });
    } else {
      initOneTime = oneTimeCharge;
    }
  }
  if (installation) {
    if (Array.isArray(installation)) {
      installation.forEach(group => {
        if (group.groupName) {
          initInstallation[group.groupName] = {
            _id: group._id,
            groupName: group.groupName,
            description: group.description,
            multiSelect: group.multiSelect,
            deleted: group.deleted,
            enabled: false, // UI state
            products: [],
            count: 0,
          };
          group.products.forEach(product => {
            initInstallation[group.groupName].products.push({
              ...product,
              changeProduct: 'Analog Lines',
              versionPrice: versionPrice,
              selected: false,
              quantity: 0,
              count: 0,
            });
          });
        }
      });
    } else {
      initInstallation = installation;
    }
  }
  productGroups.forEach(group => {
    if (!initData[group.groupName]) {
      initData[group.groupName] = {
        _id: group._id,
        name: group.groupName,
        description: group.description,
        multiSelect: group.multiSelect,
        deleted: group.deleted,
        enabled: false, // UI state
        products: [],
        count: 0,
      };
    }
    group.products.forEach(product => {
      initData[group.groupName].products.push({
        ...product,
        changeProduct: 'Analog Lines',
        versionPrice: versionPrice,
        selected: false,
        quantity: 0,
        count: 0,
      });
    });
  });

  return {
    boxId: `Box${index}`,
    defaultBoxName: `Appliance Description`,
    quantity: 1,
    boxName,
    changeProduct,
    versionPrice,
    count,
    productGroups: initData,
    oneTimeCharge: initOneTime,
    installation: initInstallation,
  };
}

function getProposalBox(productGroups, index, boxName, changeProduct, versionPrice, count) {
  const initData = {};
  Object.keys(productGroups).forEach(group => {
    if (!initData[group]) {
      initData[group] = {
        _id: productGroups[group]._id,
        name: productGroups[group].name,
        description: productGroups[group].description,
        multiSelect: productGroups[group].multiSelect,
        deleted: productGroups[group].deleted,
        enabled: false, // UI state
        products: [],
        count: count,
      };
    }
    productGroups[group].products.forEach(product => {
      initData[group].products.push({
        ...product,
      });
    });
  });
  return {
    productGroups: initData,
    boxId: `Box${index}`,
    defaultBoxName: `Appliance Description`,
    quantity: 1,
    boxName,
    changeProduct,
    versionPrice,
    count,
  };
}

function convertProposalBoxProductGroups(productGroups, commingFrom) {
  const productGroupsData = {};
  if (commingFrom === 'installation') {
    productGroups.forEach(group => {
      if (!productGroupsData[group.groupName]) {
        productGroupsData[group.groupName] = group;
      }
    });
  } else {
    productGroups.forEach(group => {
      if (!productGroupsData[group.groupName]) {
        productGroupsData[group.name] = group;
      }
    });
  }
  return productGroupsData;
}

const locations = (state = [], action) => {
  switch (action.type) {
    case 'CLEAR_PROPOSAL_LOCATIONS':
      return [];
    case 'ADD_PROPOSAL_LOCATION': {
      if (state.length && action.payload) {
        var initData = {};
        var initOneTime = {};
        var initInstallation = {};
        var oneTimeCharge = state[0].proposalBoxes[0].oneTimeCharge;
        var installation = state[0].proposalBoxes[0].installation;
        var productGroups = state[0].proposalBoxes[0].productGroups;
        if (oneTimeCharge) {
          Object.values(oneTimeCharge).forEach(group => {
            initOneTime[group.name] = {
              _id: group._id,
              name: group.name,
              description: group.description,
              multiSelect: true,
              deleted: false,
              enabled: false, // UI state
              products: [],
              count: 0,
            };

            group.products.forEach(product => {
              initOneTime[group.name].products.push({
                ...product,
                changeProduct: 'Analog Lines',
                selected: false,
                quantity: 0,
                count: 0,
              });
            });
          });
        }
        if (installation) {
          Object.values(installation).forEach(group => {
            initInstallation[group.groupName] = {
              _id: group._id,
              groupName: group.groupName,
              description: group.description,
              multiSelect: true,
              deleted: false,
              enabled: false, // UI state
              products: [],
              count: 0,
            };

            group.products.forEach(product => {
              initInstallation[group.groupName].products.push({
                ...product,
                changeProduct: 'Analog Lines',
                selected: false,
                quantity: 0,
                count: 0,
              });
            });
          });
        }

        Object.values(productGroups).forEach(group => {
          if (!initData[group.name]) {
            initData[group.name] = {
              _id: group._id,
              name: group.name,
              description: group.description,
              multiSelect: true,
              deleted: false,
              enabled: false, // UI state
              products: [],
              count: 0,
            };
            group.products.forEach(product => {
              initData[group.name].products.push({
                ...product,
                changeProduct: 'Analog Lines',
                versionPrice: product.versionPrice,
                selected: false,
                quantity: 0,
                count: 0,
              });
            });
          }
        });

        return [
          ...state,
          {
            defaultName: `Please enter location name`,
            selected: true,
            proposalBoxes: [
              {
                ...state[0].proposalBoxes[0],
                oneTimeCharge: initOneTime,
                productGroups: initData,
                installation: initInstallation,
              },
            ],
          },
        ];
      } else {
        return [
          ...state,
          {
            defaultName: `Please enter location name`,
            selected: true,
            proposalBoxes: [],
          },
        ];
      }
    }
    case 'REMOVE_PROPOSAL_LOCATION': {
      const { locationIdx } = action;
      return [...state.slice(0, locationIdx), ...state.slice(locationIdx + 1)].map(location => ({
        ...location,
        defaultName: `Please enter location name`,
      }));
    }
    case 'ADD_PROPOSAL_BOX_IN_LOCATION': {
      const {
        locationIdx,
        productGroups,
        boxName = '',
        changeProduct = 'Analog Lines',
        versionPrice,
        count,
        oneTimeCharge,
        installation,
      } = action;
      let updateLocation;
      if (productGroups && productGroups.length === 0) {
        updateLocation = {
          ...state[locationIdx],
          proposalBoxes: [
            ...state[locationIdx].proposalBoxes,
            getProposalBoxInitialData(
              productGroups,
              state[locationIdx].proposalBoxes[0],
              boxName,
              changeProduct,
              versionPrice,
              count,
              oneTimeCharge,
              installation,
            ),
          ],
        };
      } else {
        updateLocation = {
          ...state[locationIdx],
          proposalBoxes: [
            ...state[locationIdx].proposalBoxes,
            getProposalBoxInitialData(
              productGroups,
              state[locationIdx].proposalBoxes.length + 1,
              boxName,
              changeProduct,
              versionPrice,
              count,
              oneTimeCharge,
              installation,
            ),
          ],
        };
      }
      return [...state.slice(0, locationIdx), updateLocation, ...state.slice(locationIdx + 1)];
    }
    case 'REMOVE_PROPOSAL_BOX_IN_LOCATION': {
      const { locationIdx, boxIndex } = action;
      const savedOneTimeCharge = state[locationIdx].proposalBoxes[0].oneTimeCharge;
      let updateLocation = state[locationIdx];
      if (boxIndex === 0 || state[locationIdx].proposalBoxes.length <= 1) {
        toast.error(`Cannot delete first box`);
      } else {
        updateLocation = {
          ...state[locationIdx],
          proposalBoxes: [
            ...state[locationIdx].proposalBoxes.slice(0, boxIndex),
            ...state[locationIdx].proposalBoxes.slice(boxIndex + 1),
          ].map((box, index) => ({ ...box, boxId: `Box${index + 1}` })),
        };
      }
      return [...state.slice(0, locationIdx), updateLocation, ...state.slice(locationIdx + 1)];
    }
    case 'PROPOSAL_BOX_PRODUCT_QUANTITY_CHANGE_IN_LOCATION': {
      const {
        locationIdx,
        boxIndex,
        productGroupName,
        productName,
        quantity,
        selected,
        count,
      } = action;
      const locations = [...state];
      const changedBox = locations[locationIdx].proposalBoxes[boxIndex];
      if (!changedBox) {
        return state;
      }
      const group = changedBox.productGroups[productGroupName];
      if (!group) {
        return state;
      }
      const foundProduct = group.products.find(product => product.name === productName);
      if (!foundProduct) {
        return state;
      }
      foundProduct.quantity = quantity;
      foundProduct.selected = selected;
      foundProduct.count = count;
      return locations;
    }

    case 'PROPOSAL_ONETIME_QUANTITY_CHANGE_IN_LOCATION': {
      const {
        locationIdx,
        boxIndex,
        productGroupName,
        productName,
        quantity,
        selected,
        count,
      } = action;
      const locations = [...state];
      const changedBox = locations[locationIdx].proposalBoxes[boxIndex];
      if (!changedBox) {
        return state;
      }
      const group = changedBox.oneTimeCharge[productGroupName];
      if (!group) {
        return state;
      }
      const foundProduct = group.products.find(product => product.name === productName);

      if (!foundProduct) {
        return state;
      }
      foundProduct.quantity = quantity;
      foundProduct.selected = selected;
      foundProduct.count = count;

      return locations;
    }

    case 'PROPOSAL_INSTALLATION_QUANTITY_CHANGE_IN_LOCATION': {
      const {
        locationIdx,
        boxIndex,
        productGroupName,
        productName,
        quantity,
        selected,
        count,
      } = action;
      const locations = [...state];
      const changedBox = locations[locationIdx].proposalBoxes[boxIndex];
      if (!changedBox) {
        return state;
      }
      const group = changedBox.installation[productGroupName];
      if (!group) {
        return state;
      }
      const foundProduct = group.products.find(product => product.name === productName);

      if (!foundProduct) {
        return state;
      }
      foundProduct.quantity = quantity;
      foundProduct.selected = selected;
      foundProduct.count = count;

      return locations;
    }

    case 'UPDATE_PROPOSAL_BOX_PRODUCT_GROUPS_IN_LOCATION': {
      const { locationIdx, boxIndex, productGroups } = action;
      const box = state[locationIdx].proposalBoxes[boxIndex];
      if (!box) {
        return state;
      }
      const newState = [...state];
      const copyProducts = JSON.parse(JSON.stringify(productGroups));
      newState[locationIdx].proposalBoxes[boxIndex].productGroups = Array.isArray(copyProducts)
        ? convertProposalBoxProductGroups(copyProducts)
        : copyProducts;

      return newState;
    }

    case 'UPDATE_PROPOSAL_ONE_TIME_CHARGE_IN_LOCATION': {
      const { locationIdx, boxIndex, oneTimeCharge } = action;
      const box = state[locationIdx].proposalBoxes[boxIndex];
      if (!box) {
        return state;
      }
      const newState = [...state];
      const copyProducts = JSON.parse(JSON.stringify(oneTimeCharge));
      newState[locationIdx].proposalBoxes[boxIndex].oneTimeCharge = Array.isArray(copyProducts)
        ? convertProposalBoxProductGroups(copyProducts)
        : copyProducts;

      return newState;
    }

    case 'UPDATE_PROPOSAL_INSTALLATION_CHARGE_IN_LOCATION': {
      const { locationIdx, boxIndex, installation } = action;
      const box = state[locationIdx].proposalBoxes[boxIndex];
      if (!box) {
        return state;
      }
      const newState = [...state];
      const copyProducts = JSON.parse(JSON.stringify(installation));
      newState[locationIdx].proposalBoxes[boxIndex].installation = Array.isArray(copyProducts)
        ? convertProposalBoxProductGroups(copyProducts, 'installation')
        : copyProducts;

      return newState;
    }

    case 'PROPOSAL_BOX_PRODUCT_PRICE_CHANGE_IN_LOCATION': {
      const { productGroupName, productName, key, price } = action;
      const locations = JSON.parse(JSON.stringify(state));
      return locations.map(location => {
        const updateProposalBoxes = location.proposalBoxes.map(box => {
          const updateProduct = box.productGroups[productGroupName];
          const foundProduct = updateProduct.products.find(product => product.name === productName);
          if (!foundProduct) {
            return box;
          }
          const foundPrice = foundProduct.priceList.find(el => el.key === key);
          if (!foundPrice) {
            return box;
          }
          foundPrice.price = price;

          return {
            ...box,
            productGroups: { ...box.productGroups, [productGroupName]: updateProduct },
          };
        });

        return { ...location, proposalBoxes: updateProposalBoxes };
      });
    }

    case 'PROPOSAL_ONE_TIME_PRICE_CHANGE_IN_LOCATION': {
      const { productGroupName, productName, key, price } = action;
      const locations = JSON.parse(JSON.stringify(state));

      return locations.map(location => {
        const updateProposalBoxes = location.proposalBoxes.map(box => {
          const updateProduct = box.oneTimeCharge[productGroupName];
          const foundProduct = updateProduct.products.find(product => product.name === productName);
          if (!foundProduct) {
            return box;
          }

          foundProduct.price = price;

          return {
            ...box,
            oneTimeCharge: { ...box.oneTimeCharge, [productGroupName]: updateProduct },
          };
        });

        return { ...location, proposalBoxes: updateProposalBoxes };
      });
    }

    case 'PROPOSAL_INSTALLATION_PRICE_CHANGE_IN_LOCATION': {
      const { productGroupName, productName, key, price } = action;
      const locations = JSON.parse(JSON.stringify(state));
      return locations.map(location => {
        const updateProposalBoxes = location.proposalBoxes.map(box => {
          const updateProduct = box.installation[productGroupName];
          const foundProduct = updateProduct.products.find(product => product.name === productName);

          if (!foundProduct) {
            return box;
          }

          foundProduct.price = price;

          return {
            ...box,
            installation: { ...box.installation, [productGroupName]: updateProduct },
          };
        });

        return { ...location, proposalBoxes: updateProposalBoxes };
      });
    }
    case 'PROPOSAL_LOCATION_FIELD_UPDATE': {
      const { locationIdx, name, value } = action;
      const locations = [...state];
      const updateLocation = locations[locationIdx];

      return [
        ...locations.slice(0, locationIdx),
        {
          ...updateLocation,
          [name]: value,
        },
        ...locations.slice(locationIdx + 1),
      ];
    }
    case 'COPY_PROPOSAL_BOX_IN_LOCATION': {
      const { locationIdx, duplicateBox } = action;
      const locations = [...state];
      let boxIndex = locations[locationIdx].proposalBoxes.length + 1;
      let duplicateName = `duplicate-box`;
      const updateLocation = {
        ...state[locationIdx],
        proposalBoxes: [
          ...state[locationIdx].proposalBoxes,
          getProposalBox(
            duplicateBox.productGroups,
            boxIndex,
            duplicateName,
            duplicateBox.changeProduct,
            duplicateBox.versionPrice,
            duplicateBox.count,
          ),
        ],
      };
      return [
        ...locations.slice(0, locationIdx),
        updateLocation,
        ...locations.slice(+locationIdx + 1),
      ];
    }
    case 'PROPOSAL_BOX_FIELD_UPDATE': {
      const { locationIdx, boxIndex, name, value } = action;
      const locations = [...state];
      const updateLocation = locations[locationIdx];
      const updateBoxes = [
        ...updateLocation.proposalBoxes.slice(0, boxIndex),
        { ...updateLocation.proposalBoxes[boxIndex], [name]: value },
        ...updateLocation.proposalBoxes.slice(boxIndex + 1),
      ];

      return [
        ...locations.slice(0, locationIdx),
        { ...updateLocation, proposalBoxes: updateBoxes },
        ...locations.slice(+locationIdx + 1),
      ];
    }

    case 'PROPOSAL_ONETIME_FIELD_UPDATE': {
      const { locationIdx, boxIndex, name, value } = action;
      const locations = [...state];
      const updateLocation = locations[locationIdx];
      const updateBoxes = [
        ...updateLocation.proposalBoxes.slice(0, boxIndex),
        { ...updateLocation.proposalBoxes[boxIndex], [name]: value },
        ...updateLocation.proposalBoxes.slice(boxIndex + 1),
      ];

      return [
        ...locations.slice(0, locationIdx),
        { ...updateLocation, proposalBoxes: updateBoxes },
        ...locations.slice(+locationIdx + 1),
      ];
    }

    case 'PROPOSAL_INSTALLATION_FIELD_UPDATE': {
      const { locationIdx, boxIndex, name, value } = action;
      const locations = [...state];
      const updateLocation = locations[locationIdx];
      const updateBoxes = [
        ...updateLocation.proposalBoxes.slice(0, boxIndex),
        { ...updateLocation.proposalBoxes[boxIndex], [name]: value },
        ...updateLocation.proposalBoxes.slice(boxIndex + 1),
      ];

      return [
        ...locations.slice(0, locationIdx),
        { ...updateLocation, proposalBoxes: updateBoxes },
        ...locations.slice(+locationIdx + 1),
      ];
    }

    case 'COPY_LOCATION': {
      const { locationIdx } = action;
      const locationCopy = JSON.parse(JSON.stringify(state[locationIdx]));
      locationCopy.name = '';
      return [...state.slice(0, locationIdx + 1), locationCopy, ...state.slice(locationIdx + 1)];
    }
    case 'CHANGE_PRODUCT_DESCRIPTION': {
      const { locationIdx, boxIndex, productGroupName, productName, value } = action;

      const updateLocation = state[locationIdx];
      const foundProduct = updateLocation.proposalBoxes[boxIndex].productGroups[
        productGroupName
      ].products.find(product => product.name === productName);
      foundProduct.description = value;

      return [...state.slice(0, locationIdx), updateLocation, ...state.slice(locationIdx + 1)];
    }
    default:
      return state;
  }
};

const initialIsNewProposal = false;
const isNewProposal = (state = initialIsNewProposal, action) => {
  switch (action.type) {
    case 'SET_STATUS_PROPOSAL':
      return action.isNewProposal;
    default:
      return state;
  }
};

const initialIsVerizonProducts = true;
const isVerizonProducts = (state = initialIsVerizonProducts, action) => {
  switch (action.type) {
    case 'SET_VERIZON_PRODUCT_GROUPS':
      return action.isVerizonProducts;
    default:
      return state;
  }
};

const initialProductGroups = [];
const productGroups = (state = initialProductGroups, action) => {
  switch (action.type) {
    case 'GET_PRODUCT_GROUPS_SUCCESS':
    case 'GET_PRODUCT_GROUPS_VERIZON_SUCCESS':
    case 'SET_PRODUCT_GROUPS':
      return [...action.productGroups];
    case 'CHANGE_PRICE_PRODUCT_GROUPS': {
      const { productGroupName, productName, key, price } = action;

      return state.map(el => {
        if (el.groupName === productGroupName) {
          const updateProducts = el.products.map(product => {
            if (product.name === productName) {
              const updatePriceList = product.priceList.map(item => {
                if (item.key === key) {
                  return { ...item, price };
                }
                return item;
              });
              return { ...product, priceList: updatePriceList };
            }
            return product;
          });
          return { ...el, products: updateProducts };
        }
        return el;
      });
    }
    default:
      return state;
  }
};

const initialProductGroupsChange = [];
const productGroupsChange = (state = initialProductGroupsChange, action) => {
  switch (action.type) {
    case 'GET_PRODUCT_GROUPS_CHANGE_SUCCESS':
      return [...action.productGroups];
    default:
      return state;
  }
};

const initialOneTimeCharge = [];
const oneTimeCharge = (state = initialOneTimeCharge, action) => {
  switch (action.type) {
    case 'GET_ONE_TIME_CHARGE_GROUP_SUCCESS':
      return [...action.oneTimeCharge];
    case 'SET_ONETIME_CHARGE':
      return [...action.oneTimeCharge];
    case 'CHANGE_PRICE_ONE_TIME_CHARGE': {
      const { productGroupName, productName, key, price } = action;
      return state.map(el => {
        if (el.groupName === productGroupName) {
          const updateProducts = el.products.map(product => {
            if (product.name === productName) {
              return { ...product };
            }
            return product;
          });
          return { ...el, products: updateProducts };
        }
        return el;
      });
    }
    default:
      return state;
  }
};

const initialInstallation = [];
const installation = (state = initialInstallation, action) => {
  switch (action.type) {
    case 'SET_INSTALLATION':
      return [...action.installation];
    case 'GET_INSTALLATION_SUCCESS':
      return [...action.installation];
    case 'CHANGE_PRICE_ONE_TIME_CHARGE': {
      const { productGroupName, productName, key, price } = action;
      return state.map(el => {
        if (el.groupName === productGroupName) {
          const updateProducts = el.products.map(product => {
            if (product.name === productName) {
              return { ...product };
            }
            return product;
          });
          return { ...el, products: updateProducts };
        }
        return el;
      });
    }
    default:
      return state;
  }
};

const initialDeleteLocation = {
  showDeleteModal: false,
  deleteLocationIndex: undefined,
  deleteLocationName: '',
};

const deleteLocation = (state = initialDeleteLocation, action) => {
  switch (action.type) {
    case 'DELETE_LOCATION_SHOW': {
      const { locationIdx, locationName } = action;
      return {
        showDeleteModal: true,
        deleteLocationIndex: locationIdx,
        deleteLocationName: locationName,
      };
    }
    case 'DELETE_LOCATION_CANCEL':
    case 'DELETE_LOCATION_CONFIRM': {
      return initialDeleteLocation;
    }
    default:
      return state;
  }
};

const initialDownloadDocs = {
  showDownloadModal: false,
};
const downloadDocs = (state = initialDownloadDocs, action) => {
  switch (action.type) {
    case 'DOWLOAD_DOCS_SHOW': {
      return { showDownloadModal: true };
    }
    case 'DOWNLOAD_DOCS_CANCEL': {
      return initialDownloadDocs;
    }
    default:
      return state;
  }
};

const initialStateCountry = [];
const stateCountry = (state = initialStateCountry, action) => {
  switch (action.type) {
    case 'GET_STATE_COUNTRY_SUCCESS':
      return [...action.stateCountry];
    default:
      return state;
  }
};

const defaultStateCodes = [];
const stateCodes = (state = defaultStateCodes, action) => {
  switch (action.type) {
    case 'GET_STATE_CODES_SUCCESS':
      return [...action.payload];
    default:
      return state;
  }
};

const initialCityOfState = [];
const cityOfState = (state = initialCityOfState, action) => {
  switch (action.type) {
    case 'GET_CITY_OF_STATE_SUCCESS':
      return [...action.cityOfState];
    default:
      return state;
  }
};

const initialTemplateEmailList = [];
const templateEmailList = (state = initialTemplateEmailList, action) => {
  switch (action.type) {
    case 'GET_TEMPLATE_EMAIL_LIST_SUCCESS':
      return [...action.getTemplateEmailList];
    default:
      return state;
  }
};

const initialApplyTemplate = '';
const applyTemplate = (state = initialApplyTemplate, action) => {
  switch (action.type) {
    case 'APPLY_TEMPLATE_SUCCESS':
      return action.applyTemplate;
    default:
      return state;
  }
};

const initialApplyTemplateForEdit = '';
const applyTemplateForEdit = (state = initialApplyTemplateForEdit, action) => {
  switch (action.type) {
    case 'APPLY_TEMPLATE_FOR_EDIT_SUCCESS':
      return action.applyTemplateForEdit;
    default:
      return state;
  }
};

export default combineReducers({
  formState: proposalEditor,
  proposalObject,
  locations,
  displayMode,
  isNewProposal,
  isVerizonProducts,
  productGroups,
  oneTimeCharge,
  installation,
  deleteLocation,
  downloadDocs,
  productGroupsChange,
  stateCountry,
  cityOfState,
  templateEmailList,
  applyTemplate,
  applyTemplateForEdit,
  stateCodes,
});
